import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CircleConfig, DEFAULT_CIRCLE_CONFIG } from "@/types/circle";
import { toast } from "sonner";

export function useCircleConfig() {
  const queryClient = useQueryClient();

  const { data: config, isLoading } = useQuery({
    queryKey: ["circle-config"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", "circle_config")
        .maybeSingle();

      if (error) throw error;
      
      if (!data) {
        return DEFAULT_CIRCLE_CONFIG;
      }
      
      const storedValue = data.value as unknown as Partial<CircleConfig>;
      return { ...DEFAULT_CIRCLE_CONFIG, ...storedValue };
    },
  });

  const updateConfig = useMutation({
    mutationFn: async (newConfig: Partial<CircleConfig>) => {
      const currentConfig = config || DEFAULT_CIRCLE_CONFIG;
      const mergedConfig = { ...currentConfig, ...newConfig };

      const { error } = await supabase
        .from("app_settings")
        .upsert({
          key: "circle_config",
          value: mergedConfig,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;
      return mergedConfig;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["circle-config"] });
      toast.success("Circle settings updated");
    },
    onError: (error) => {
      console.error("Failed to update circle config:", error);
      toast.error("Failed to update settings");
    },
  });

  return {
    config: config || DEFAULT_CIRCLE_CONFIG,
    isLoading,
    updateConfig,
  };
}
